import { supabase } from './supabase';
import { rubroVisible } from './onboardingPendiente';
import { onboardingPendiente } from './onboardingPendiente';

/**
 * Asegura que la empresa tenga su marca DEFAULT en `brand_profiles`.
 *
 * Hasta ahora el onboarding de registro solo creaba la empresa
 * (`company_profiles`) — la marca quedaba únicamente como `company_profiles.nombre`
 * y NO se creaba la fila en `brand_profiles` (solo ~184 de 8484 empresas la
 * tenían, vía el flujo aparte "Mi marca"). Este helper la crea al registrarse.
 *
 * Es el MISMO patrón (probado en prod) que el safety-net de `BrandDescription`:
 * crea la default a partir de `company_profiles` y reasigna los productos sin
 * marca a ella.
 *
 * Garantías:
 *  - **Idempotente:** si ya existe una marca default (`is_default=true`,
 *    `is_draft=false`) no hace nada → seguro de llamar más de una vez.
 *  - **Best-effort para crear:** un fallo al crear la marca se loguea y se ignora;
 *    nunca debe romper el onboarding. La REPARACIÓN del rubro es la excepción y se
 *    reporta por el valor de retorno (ver abajo).
 *  - La marca arranca "pelada" (sin colores/fuentes): la identidad con IA se
 *    completa después con el flujo "Mejorá tus fotos" (`generar_identidad`), que
 *    en modo default ACTUALIZA esta misma fila (no crea otra).
 *
 * `rubroReal` REPARA una marca que nació sin él. Hace falta desde el alta por link
 * invertida: ahí la empresa se crea al registrarse —para poder cobrar en un paso— con el
 * centinela `sin-definir`, y esta función copia ESE valor a la marca. Cuando el
 * cuestionario post-pago pone el rubro de verdad, la marca ya existe y el `return` de
 * abajo la dejaba con el centinela PARA SIEMPRE.
 *
 * Y eso no es cosmético: los modos se gatean por `brand_profiles.rubro` con igualdad
 * exacta (`lib/rubro.ts`) y NO caen al de la empresa, así que un cliente que pagó el plan
 * más caro se quedaba sin Plantillas, sin Diseño de indumentaria, sin Moodboards, sin
 * Catálogo de muebles y sin Infografías. Peor: como el centinela es un string NO VACIO,
 * también anula los fallbacks del tipo `marca?.rubro ?? perfil?.rubro`, que con `null`
 * habrían funcionado solos.
 *
 * Nunca pisa un rubro REAL: sólo escribe si el de la marca sigue pendiente.
 * Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
 *
 * **Devuelve `false` sólo cuando la REPARACIÓN del rubro falló.** Todo lo demás sigue
 * siendo best-effort y devuelve `true`, incluido el caso "no había nada que reparar".
 * La reparación es la excepción porque es el último eslabón que le devuelve los modos a
 * alguien que ya pagó, y si se pierde no hay nada más adelante que la reintente: el
 * cuestionario ya escribió el rubro de la empresa, así que el rebote de `App` no vuelve
 * a traerlo y `createCompanyForUser` tampoco lo llama de nuevo. Tragarse ese error
 * dejaba la marca con el rubro en blanco PARA SIEMPRE, y los gates de modos leen
 * `brand_profiles.rubro` con igualdad exacta, sin caer al de la empresa.
 */
export async function ensureDefaultBrand(
  companyId: string,
  rubroReal?: string | null,
): Promise<boolean> {
  if (!companyId) return true;
  try {
    const { data: defaultBrand } = await supabase
      .from('brand_profiles')
      .select('id, rubro')
      .eq('id_company', companyId)
      .eq('is_default', true)
      .eq('is_draft', false)
      .limit(1)
      .maybeSingle();
    if (defaultBrand) {
      // Ya tiene marca default → no duplicar. Pero si nació sin rubro y ahora sí lo
      // sabemos, se lo escribimos: es el caso del alta por link (ver el JSDoc).
      const fila = defaultBrand as { id: string; rubro?: string | null };
      if (
        rubroReal &&
        !onboardingPendiente(rubroReal) &&
        onboardingPendiente(fila.rubro)
      ) {
        const { error: errorRubro } = await supabase
          .from('brand_profiles')
          .update({ rubro: rubroReal })
          .eq('id', fila.id);
        if (errorRubro) {
          console.error('[ensureDefaultBrand] no se pudo reparar el rubro:', errorRubro);
          return false;
        }
      }
      return true;
    }

    const { data: companyRow } = await supabase
      .from('company_profiles')
      .select('nombre, rubro, logo_url')
      .eq('id_company', companyId)
      .maybeSingle();

    const name =
      (companyRow as { nombre?: string } | null)?.nombre?.trim() ||
      'Marca principal';
    // El centinela NO se copia a la marca. La empresa nace con "sin-definir" para poder
    // cobrar en un paso (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues),
    // pero la marca es lo que se pinta en la sidebar, en el selector y en Mis marcas, y
    // ahí ese valor se leía crudo. Vacío es el estado que esas pantallas ya manejan, y
    // `onboardingPendiente('')` sigue siendo `true`, así que el cuestionario post-pago
    // lo pisa igual (`altaDesdeCuestionario` sólo actualiza si sigue pendiente).
    const rubro = rubroVisible((companyRow as { rubro?: string } | null)?.rubro) ?? '';
    const logo = (companyRow as { logo_url?: string } | null)?.logo_url || null;

    const { data: created, error: insErr } = await supabase
      .from('brand_profiles')
      .insert({
        id_company: companyId,
        name,
        rubro,
        logo_url: logo,
        context: '',
        colors: [],
        is_default: true,
        is_draft: false,
      })
      .select('id')
      .single();
    if (insErr || !created?.id) {
      if (insErr) console.error('[ensureDefaultBrand] insert error:', insErr);
      // Sigue siendo best-effort: la marca se crea sola en varios lugares más (el
      // safety-net de `BrandDescription`, la primera generación), así que no hay un
      // último eslabón que proteger como en la reparación de arriba.
      return true;
    }

    // Reasignar los productos sin marca a la nueva default (mismo backfill que
    // el safety-net de BrandDescription).
    await supabase
      .from('productos')
      .update({ brand_id: created.id })
      .eq('id_company', companyId)
      .is('brand_id', null);
    return true;
  } catch (e) {
    console.error('[ensureDefaultBrand] error:', e);
    // Una excepción acá puede haber volteado la reparación tanto como el insert. El
    // caller que la necesita (el cuestionario) prefiere pedir un reintento barato antes
    // que dejar a alguien que pagó sin sus modos.
    return false;
  }
}
